/* ==========================================================================
   Quick-add on the listing.

   Resolves a variant before adding. 0007 §6 forbids adding a plan blind
   because Advanced on .in carries an Android/iOS selector, and 10 of 12 .in
   products carry two or more variants — one of them twenty.

   The submit is a real form POST. gp-cart.js already intercepts any form
   posting to /cart/add, adds over fetch and opens the drawer, and gp-checker.js
   attaches the session's compatibility check as cart attributes on the capture
   phase of the same event. Nothing here touches the cart.
   ========================================================================== */
(() => {
  'use strict';

  class QuickAdd {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-gp-qa-form]');
      this.idEl = root.querySelector('[data-gp-qa-id]');
      this.priceEl = root.querySelector('[data-gp-qa-price]');
      this.submitEl = root.querySelector('[data-gp-qa-submit]');
      if (!this.form || !this.idEl || !this.submitEl) return;

      try {
        this.variants = JSON.parse(root.querySelector('[data-gp-qa-variants]').textContent);
      } catch { this.variants = []; }

      // Positions come from the RENDERED option rows, so an option the snippet
      // skipped for having one value never enters the match. That is correct:
      // every variant carries that value, so the axis cannot distinguish them.
      this.positions = [...root.querySelectorAll('[data-gp-qa-option]')]
        .map((el) => Number(el.dataset.gpQaOption));
      this.selection = {};
      this.bind();
    }

    bind() {
      this.root.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-gp-qa-value]');
        if (!btn) return;
        e.preventDefault();
        const pos = Number(btn.dataset.gpQaPosition);
        this.selection[pos] = btn.dataset.gpQaValue;
        this.root
          .querySelectorAll(`[data-gp-qa-position="${pos}"]`)
          .forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
        this.sync();
      });

      // The drawer is about to cover this panel, and it must not still be open
      // underneath when the buyer closes it. gp-cart.js reads the form on the
      // bubble phase at `document`; this listener runs first, and a closed
      // <details> still yields a complete FormData.
      this.form.addEventListener('submit', () => { this.root.open = false; });
    }

    match() {
      if (this.positions.some((p) => !this.selection[p])) return null;
      return this.variants.find((v) =>
        this.positions.every((p) => v.options[p - 1] === this.selection[p])
      ) || null;
    }

    sync() {
      const v = this.match();
      // An unbuyable combination stays unsubmittable. With nothing out of
      // stock this means the combination has no SKU behind it.
      const ok = !!v && v.available;
      this.idEl.value = ok ? v.id : '';
      this.submitEl.disabled = !ok;
      this.submitEl.setAttribute('aria-disabled', String(!ok));
      if (v && this.priceEl) this.priceEl.textContent = v.price;
    }
  }

  const open = () => [...document.querySelectorAll('[data-gp-qa][open]')];

  const init = (scope) => {
    (scope || document).querySelectorAll('[data-gp-qa]').forEach((el) => {
      if (el.dataset.gpQaReady) return;
      el.dataset.gpQaReady = '1';
      try { new QuickAdd(el); } catch (err) { console.warn('[gp-quick-add]', err); }
    });
  };

  /* A <details> closes only by clicking its own summary again, which for a
     panel floating over the neighbouring cards is a trap. One open at a time,
     Escape closes, and a click anywhere outside closes — the behaviour of every
     other popover on the site. Delegated once, so it costs nothing per card. */
  /* The panel opens upward by default, over the card's own body — the right
     direction, since that is the product being chosen. On the first row of a
     grid there may be no room above and nothing to scroll, so measure and flip
     downward when below is genuinely the roomier side. The 96px allowance is
     the sticky sort bar, which would otherwise cover the panel's head.

     scrollIntoView carries no `behavior`, so it is instant and there is nothing
     here for prefers-reduced-motion to object to. */
  const place = (el) => {
    const panel = el.querySelector('.gp-qa__panel');
    const btn = el.querySelector('.gp-qa__btn');
    if (!panel || !btn) return;
    const r = btn.getBoundingClientRect();
    const need = panel.offsetHeight + 8;
    const above = r.top - 96;
    const below = window.innerHeight - r.bottom;
    el.classList.toggle('gp-qa--below', above < need && below > above);
    panel.scrollIntoView({ block: 'nearest' });
  };

  document.addEventListener('toggle', (e) => {
    const el = e.target;
    if (!(el instanceof HTMLElement) || !el.hasAttribute('data-gp-qa') || !el.open) return;
    open().forEach((other) => { if (other !== el) other.open = false; });
    place(el);
  }, true);

  document.addEventListener('pointerdown', (e) => {
    open().forEach((el) => { if (!el.contains(e.target)) el.open = false; });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const el = e.target instanceof Element ? e.target.closest('[data-gp-qa][open]') : null;
    if (el) { el.open = false; el.querySelector('summary')?.focus(); return; }
    open().forEach((o) => { o.open = false; });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
